import React from 'react';
import { LungWeatherPattern, LungWeatherWind, bearingToCompass } from '../../models/lung-weather';
import { lungWeatherColors } from './colors';

export interface IVaneProps {
  patterns: LungWeatherPattern[];
  forecast?: LungWeatherWind;
  /** Half-width of the shaded match zone, in degrees - the direction reference spread. */
  toleranceDegrees?: number;
}

const SIZE = 262;
const CENTRE = 131;
const RADIUS = 100;

/** Gust that maps to a full-length needle, so needle length reads as wind strength. */
const FULL_SCALE_GUST = 80;

/** Compass bearings put a point on the circle: 0 degrees is up, and angles run clockwise. */
const point = (degrees: number, radius: number) => {
  const radians = (degrees * Math.PI) / 180;

  return {
    x: CENTRE + radius * Math.sin(radians),
    y: CENTRE - radius * Math.cos(radians)
  };
};

const needleRadius = (gust: number) => Math.max(28, Math.min(RADIUS - 5, (gust / FULL_SCALE_GUST) * (RADIUS - 5)));

/**
 * A weather vane: each stored pattern is a thin needle at the direction the wind came from before that outbreak,
 * its length the gust. The thick needle is this week's forecast.
 *
 * This is the whole verdict in one picture. When six needles point west and the forecast lands beside the one
 * pointing east, it is immediately obvious both that the forecast matches something and that averaging the
 * patterns would have pointed nowhere near it.
 */
const Vane: React.FC<IVaneProps> = ({ patterns, forecast, toleranceDegrees = 30 }) => {
  const closest = patterns.find((pattern) => pattern.isClosestMatch);

  // The arc showing how far the wind may sit from the matched pattern and still count.
  const zone = closest
    ? {
        from: point(closest.wind.dominantWindDirectionDegrees - toleranceDegrees, RADIUS),
        to: point(closest.wind.dominantWindDirectionDegrees + toleranceDegrees, RADIUS)
      }
    : null;

  return (
    <svg
      viewBox={`0 0 ${SIZE} ${SIZE + 2}`}
      style={{ display: 'block', width: '100%', maxWidth: '19rem', height: 'auto' }}
      role="img"
      aria-label={
        forecast
          ? `Weather vane. ${patterns.length} recorded wind patterns. The forecast wind comes from ` +
            `${Math.round(forecast.dominantWindDirectionDegrees)} degrees, ` +
            `${bearingToCompass(forecast.dominantWindDirectionDegrees)}, gusting ` +
            `${Math.round(forecast.maxWindGustKmh)} kilometres per hour.`
          : `Weather vane showing ${patterns.length} recorded wind patterns. No forecast has been compared yet.`
      }
    >
      {zone && (
        <path
          d={`M ${CENTRE} ${CENTRE} L ${zone.from.x.toFixed(1)} ${zone.from.y.toFixed(1)} A ${RADIUS} ${RADIUS} 0 0 1 ${zone.to.x.toFixed(1)} ${zone.to.y.toFixed(1)} Z`}
          fill={lungWeatherColors.accentSoft}
        />
      )}

      <circle cx={CENTRE} cy={CENTRE} r={RADIUS} fill="none" stroke={lungWeatherColors.hairline} />
      <circle
        cx={CENTRE}
        cy={CENTRE}
        r={RADIUS * 0.66}
        fill="none"
        stroke={lungWeatherColors.hairline}
        strokeDasharray="1 4"
      />
      <line x1={CENTRE} y1={CENTRE - RADIUS} x2={CENTRE} y2={CENTRE + RADIUS} stroke={lungWeatherColors.grid} />
      <line x1={CENTRE - RADIUS} y1={CENTRE} x2={CENTRE + RADIUS} y2={CENTRE} stroke={lungWeatherColors.grid} />

      {patterns.map((pattern) => {
        const tip = point(pattern.wind.dominantWindDirectionDegrees, needleRadius(pattern.wind.maxWindGustKmh));

        return (
          <line
            key={pattern.id}
            x1={CENTRE}
            y1={CENTRE}
            x2={tip.x.toFixed(1)}
            y2={tip.y.toFixed(1)}
            stroke={pattern.isClosestMatch ? lungWeatherColors.accent : lungWeatherColors.muted}
            strokeWidth={pattern.isClosestMatch ? 2.5 : 2}
            strokeOpacity={pattern.isClosestMatch ? 0.9 : 0.55}
            strokeLinecap="round"
          />
        );
      })}

      {forecast && (
        <line
          x1={CENTRE}
          y1={CENTRE}
          x2={point(forecast.dominantWindDirectionDegrees, needleRadius(forecast.maxWindGustKmh)).x.toFixed(1)}
          y2={point(forecast.dominantWindDirectionDegrees, needleRadius(forecast.maxWindGustKmh)).y.toFixed(1)}
          stroke={lungWeatherColors.accent}
          strokeWidth={4}
          strokeLinecap="round"
          // Dashed when the wind had no settled direction, because then the bearing is an artefact of averaging
          // and the score discounted it. Showing a solid needle would overstate what the forecast actually said.
          strokeDasharray={forecast.windDirectionConstancy < 0.4 ? '5 3' : undefined}
        />
      )}

      <circle
        cx={CENTRE}
        cy={CENTRE}
        r={5}
        fill={lungWeatherColors.surface}
        stroke={lungWeatherColors.accent}
        strokeWidth={2}
      />

      <text x={CENTRE} y={12} textAnchor="middle" fontSize={11} fill={lungWeatherColors.muted} letterSpacing="0.08em">
        N
      </text>
      <text
        x={SIZE - 18}
        y={CENTRE + 4}
        textAnchor="middle"
        fontSize={11}
        fill={lungWeatherColors.muted}
        letterSpacing="0.08em"
      >
        E
      </text>
      <text
        x={CENTRE}
        y={CENTRE + RADIUS + 16}
        textAnchor="middle"
        fontSize={11}
        fill={lungWeatherColors.muted}
        letterSpacing="0.08em"
      >
        S
      </text>
      <text
        x={8}
        y={CENTRE + 4}
        textAnchor="middle"
        fontSize={11}
        fill={lungWeatherColors.muted}
        letterSpacing="0.08em"
      >
        W
      </text>
    </svg>
  );
};

export default Vane;
